import { useCallback, useEffect, useMemo, useState } from 'react';
import { Avatar, Card, DataTable, EmptyState, PageHeader, TextInput } from '@/components/ui';
import type { DataTableColumn } from '@/components/ui/DataTable';
import { Icon } from '@/components/ui/Icon';
import { toast } from '@/components/ui/Toast';
import { adminApi, type ReportDef } from '@/shared/api/adminApi';
import { apiClient } from '@/shared/api/client';
import { KpiStrip, type Kpi } from './reports/KpiStrip';
import { PillSelect } from './reports/PillSelect';
import { GrowBar } from './reports/GrowBar';

/**
 * "ביצועים" — דוחות המוקד (ביצועי נציגים, SLA תורים, ROI קמפיינים, נפח
 * שיחות, QA, סנטימנט, פילוח תוצאות…) שהשרת כבר ידע להפיק (/admin/reports)
 * ולא היה להם מסך אצל המנהל (07/09/2026). מציג כל דוח כטבלה גנרית
 * (שורות = רשומות, עמודות = השדות) עם טווח תאריכים וייצוא CSV.
 * 28/09/2026 (GoCall v2): בורר דוח + טווח (היום/שבוע/חודש/מותאם) בסרגל
 * אחד, פס מדדים מסוכם מהשורות עצמן, וטבלה שקטה עם נציג+אווטאר, משכי זמן
 * בפורמט שעון ואחוזים עם פס.
 */
type Scalar = string | number | boolean | null;
type Row = Record<string, Scalar>;

const LABELS: Record<string, string> = {
  agent: 'נציג', 'agent.name': 'נציג', 'agent.id': 'מזהה', 'agent.extension': 'שלוחה',
  calls: 'שיחות', answered: 'נענו', aht_sec: 'זמן שיחה ממוצע', total_talk_sec: 'סה״כ זמן שיחה',
  queue_id: 'מזהה תור', queue_name: 'תור', total: 'סה״כ', abandoned: 'ננטשו', answer_rate: 'אחוז מענה',
  date: 'תאריך', count: 'כמות', name: 'שם', campaign: 'קמפיין', leads: 'לידים', won: 'נסגרו', revenue: 'הכנסות',
  cost: 'עלות', roi: 'ROI', avg_score: 'ציון ממוצע', distribution: 'התפלגות', negative: 'שליליות', missed: 'לא נענו',
  last_at: 'אחרון', number: 'מספר', disposition: 'תוצאה', sentiment: 'סנטימנט', pct: 'אחוז', score: 'ציון',
  deals: 'עסקאות', conversion_rate: 'אחוז המרה', 'campaign.name': 'קמפיין', 'campaign.status': 'סטטוס',
  evaluations: 'הערכות', negative_calls: 'שיחות שליליות', missed_calls: 'שיחות שלא נענו', total_calls: 'סה״כ שיחות',
  risk_score: 'ציון סיכון', label: 'תוצאה', code: 'קוד', positive: 'חיוביות', neutral: 'ניטרליות',
};

function iso(d: Date): string { return d.toISOString().slice(0, 10); }

/** רשומה שטוחה: אובייקטים מקוננים הופכים ל-"parent.child". */
function flatten(value: unknown, prefix = ''): Row {
  const out: Row = {};
  if (value === null || typeof value !== 'object') { out[prefix || 'value'] = value as Scalar; return out; }
  for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
    const key = prefix ? `${prefix}.${k}` : k;
    if (v !== null && typeof v === 'object' && !Array.isArray(v)) Object.assign(out, flatten(v, key));
    else if (Array.isArray(v)) out[key] = v.length ? JSON.stringify(v) : '';
    else out[key] = v as Scalar;
  }
  return out;
}

/** מנרמל כל צורת תשובה לטבלה אחת: מערך רשומות, מפה, או אובייקט של ערכים. */
function toRows(data: unknown): Row[] {
  if (Array.isArray(data)) return data.map((r) => flatten(r));
  if (data && typeof data === 'object') {
    const entries = Object.entries(data as Record<string, unknown>);
    const allScalar = entries.every(([, v]) => v === null || typeof v !== 'object');
    if (allScalar) return entries.map(([k, v]) => ({ 'שדה': k, 'ערך': v as Scalar }));
    // מפה של אובייקטים (למשל לפי מזהה) → שורה לכל מפתח.
    return entries.map(([k, v]) => ({ 'מפתח': k, ...flatten(v) }));
  }
  return [];
}

/** שניות → "1:02:05" / "4:10" (כמו זמן שיחה בעיצוב). */
function clock(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), r = s % 60;
  const mm = h ? String(m).padStart(2, '0') : String(m);
  return `${h ? `${h}:` : ''}${mm}:${String(r).padStart(2, '0')}`;
}

const isSec = (c: string) => c.endsWith('_sec');
const isRate = (c: string) => c.endsWith('_rate') || c === 'pct';
/** עמודת "שם" שמקבלת תצוגה מודגשת (ואווטאר לנציג). */
const isName = (c: string) => c === 'agent.name' || c === 'campaign.name' || c === 'queue_name';

/** מדדים לסיכום בראש הדוח — רק סכומים של מונים שכבר בשורות. */
const SUM_KEYS = ['calls', 'total', 'total_calls', 'answered', 'abandoned', 'missed_calls', 'leads', 'deals', 'evaluations', 'count'];

function totals(rows: Row[], columns: string[]): Kpi[] {
  const sum = (k: string) => rows.reduce((t, r) => t + (typeof r[k] === 'number' ? (r[k] as number) : 0), 0);
  const out: Kpi[] = SUM_KEYS.filter((k) => columns.includes(k)).map((k) => ({ label: LABELS[k] ?? k, value: sum(k).toLocaleString('he-IL'), num: sum(k) }));
  const base = columns.includes('calls') ? 'calls' : columns.includes('total') ? 'total' : null;
  if (base && columns.includes('answered')) {
    const idx = out.findIndex((k) => k.label === LABELS.answered) + 1;
    const rate = (sum('answered') / Math.max(1, sum(base))) * 100;
    out.splice(idx, 0, { label: 'אחוז מענה', value: `${Math.round(rate)}%`, num: rate, format: (v) => `${Math.round(v)}%` });
  }
  if (columns.includes('leads') && columns.includes('deals')) {
    const conv = (sum('deals') / Math.max(1, sum('leads'))) * 100;
    out.push({ label: 'אחוז המרה', value: `${conv.toFixed(1)}%`, num: conv, format: (v) => `${v.toFixed(1)}%` });
  }
  if (columns.includes('total_talk_sec')) out.push({ label: LABELS.total_talk_sec, value: clock(sum('total_talk_sec')), num: sum('total_talk_sec'), format: clock });
  return out.slice(0, 6);
}

type Preset = 'today' | 'week' | 'month' | 'custom';
const PRESETS: { id: Preset; he: string; days: number }[] = [
  { id: 'today', he: 'היום', days: 0 },
  { id: 'week', he: 'שבוע', days: 7 },
  { id: 'month', he: 'חודש', days: 30 },
];

function fmt(v: Scalar): string {
  if (v === null || v === undefined) return '—';
  if (typeof v === 'boolean') return v ? 'כן' : 'לא';
  if (typeof v === 'number') return Number.isInteger(v) ? v.toLocaleString('he-IL') : v.toFixed(1);
  return String(v);
}

export function Reports() {
  const [defs, setDefs] = useState<ReportDef[]>([]);
  const [active, setActive] = useState<ReportDef | null>(null);
  const [preset, setPreset] = useState<Preset>('month');
  const [from, setFrom] = useState(() => iso(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(() => iso(new Date()));
  const [data, setData] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<Date | null>(null);
  // מתחלף רק בהפקה חדשה (דוח/טווח) — השורות נכנסות מחדש, כמו dashKey בעיצוב.
  const [runId, setRunId] = useState(0);

  useEffect(() => {
    adminApi.reports().then((d) => { setDefs(d); setActive((a) => a ?? d[0] ?? null); }).catch(() => toast('שגיאה בטעינת הדוחות', 'danger'));
  }, []);

  const run = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    try { setData(await adminApi.runReport(active.id, { from, to })); setFetchedAt(new Date()); setRunId((n) => n + 1); }
    catch { toast('שגיאה בהפקת הדוח', 'danger'); setData(null); }
    finally { setLoading(false); }
  }, [active, from, to]);

  useEffect(() => { void run(); }, [run]);

  function pickPreset(p: Preset) {
    setPreset(p);
    const def = PRESETS.find((x) => x.id === p);
    if (!def) return; // "טווח מותאם" — משאירים את התאריכים ופותחים את השדות.
    setFrom(iso(new Date(Date.now() - def.days * 86400000)));
    setTo(iso(new Date()));
  }

  const rows = useMemo(() => toRows(data), [data]);
  const columns = useMemo(() => Array.from(rows.reduce((set, r) => { Object.keys(r).forEach((k) => set.add(k)); return set; }, new Set<string>())), [rows]);
  // מזהה פנימי מוסתר כשיש לצדו שם (נשאר בייצוא ה-CSV).
  const visible = useMemo(() => columns.filter((c) => {
    if (c.endsWith('.id')) return !columns.includes(c.replace(/\.id$/, '.name'));
    if (c === 'queue_id') return !columns.includes('queue_name');
    return true;
  }), [columns]);
  const kpis = useMemo(() => (Array.isArray(data) ? totals(rows, columns) : []), [data, rows, columns]);

  const tableCols: DataTableColumn<Row>[] = visible.map((c) => ({
    key: c,
    he: LABELS[c] ?? c,
    // מיון לעמודות מספריות (DataTable ממיין מספרית לפי הערך הגולמי בשורה).
    sortable: rows.some((r) => typeof r[c] === 'number'),
    render: (r: Row) => {
      const v = r[c] ?? null;
      if (isName(c) && typeof v === 'string') {
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 600, minWidth: 0 }}>
            {c === 'agent.name' && <Avatar name={v} idx={Number(r['agent.id'] ?? 0)} size={30} />}
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v}</span>
          </span>
        );
      }
      if (typeof v === 'number' && isSec(c)) return <span style={{ fontVariantNumeric: 'tabular-nums' }} dir="ltr">{clock(v)}</span>;
      if (typeof v === 'number' && isRate(c)) {
        return (
          <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 110 }}>
            <GrowBar pct={v} />
            <span style={{ fontVariantNumeric: 'tabular-nums', fontSize: 'var(--t-sm)', minWidth: 38 }} dir="ltr">{Math.round(v)}%</span>
          </span>
        );
      }
      if (typeof v === 'number') {
        return <span style={{ fontVariantNumeric: 'tabular-nums', ...(c === 'deals' || c === 'won' ? { fontWeight: 600, color: 'var(--ok)' } : null) }} dir="ltr">{fmt(v)}</span>;
      }
      return fmt(v);
    },
  }));

  async function exportCsv() {
    if (!active) return;
    setExporting(true);
    try {
      const res = await apiClient.get(`/admin/reports/${active.id}/export`, { params: { from, to }, responseType: 'blob' });
      const url = URL.createObjectURL(res.data as Blob);
      const a = document.createElement('a'); a.href = url; a.download = `${active.id}-${from}-${to}.csv`; a.click();
      URL.revokeObjectURL(url);
    } catch { toast('הייצוא נכשל', 'danger'); } finally { setExporting(false); }
  }

  return (
    <div className="stack page-enter" style={{ padding: '14px 16px 40px', maxWidth: 1280, width: '100%' }}>
      <PageHeader title="ביצועים" />

      {/* סרגל: דוח + טווח בצד אחד, "נכון ל" + ייצוא בצד השני */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {defs.length > 0 && active && (
          <PillSelect
            label="דוח" ariaLabel="דוח" value={active.id}
            options={defs.map((d) => ({ value: d.id, label: d.he }))}
            onChange={(id) => setActive(defs.find((d) => d.id === id) ?? null)}
          />
        )}
        {defs.length === 0 && <span style={{ color: 'var(--muted)', fontSize: 'var(--t-sm)' }}>טוען דוחות…</span>}
        <div style={{ maxWidth: '100%', overflowX: 'auto', padding: 2, margin: -2 }}>
        <div role="tablist" aria-label="טווח" className="seg" style={{ background: 'var(--surface)', boxShadow: 'var(--shadow-1)' }}>
          {PRESETS.map((p) => (
            <button key={p.id} type="button" role="tab" aria-selected={preset === p.id} onClick={() => pickPreset(p.id)}>{p.he}</button>
          ))}
          <button type="button" role="tab" aria-selected={preset === 'custom'} onClick={() => pickPreset('custom')}>
            <Icon name="calendar" size={14} /> טווח מותאם
          </button>
        </div>
        </div>
        {preset === 'custom' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <TextInput type="date" value={from} onChange={(e) => setFrom(e.target.value)} dir="ltr" aria-label="מתאריך" style={{ height: 38, width: 150 }} />
            <span style={{ color: 'var(--muted)' }}>–</span>
            <TextInput type="date" value={to} onChange={(e) => setTo(e.target.value)} dir="ltr" aria-label="עד תאריך" style={{ height: 38, width: 150 }} />
          </div>
        )}
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 'var(--t-sm)', color: 'var(--muted)', fontVariantNumeric: 'tabular-nums' }}>
          {loading ? 'מפיק…' : fetchedAt ? `הנתונים נכונים ל־${fetchedAt.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}` : ''}
        </span>
        {active && (
          <button type="button" className="btn" onClick={() => void exportCsv()} disabled={exporting || rows.length === 0}>
            <Icon name="download" size={14} /> {exporting ? 'מייצא…' : 'ייצוא CSV'}
          </button>
        )}
      </div>

      {kpis.length > 0 && <KpiStrip items={kpis} ariaLabel={`סיכום ${active?.he ?? ''}`} delay=".05s" />}

      <Card padded={false} style={{ overflow: 'hidden', ['--d' as string]: '.1s' }}>
        {!loading && rows.length === 0 ? (
          <EmptyState icon="chart" title="אין נתונים לטווח הזה" description="נסה טווח תאריכים רחב יותר." />
        ) : (
          <div data-testid="report-table" key={runId}>
            <DataTable<Row> columns={tableCols} rows={rows} loading={loading && rows.length === 0} caption={active?.he} />
          </div>
        )}
      </Card>
    </div>
  );
}
